import {
  cloneDeep,
  isString,
  isObject,
  isArray,
  get,
  set,
  startsWith,
  isFunction,
  isEqual,
  extend,
  isNumber,
  isBoolean,
  map,
} from 'lodash-es';

class ResourceHistory {
  readonly $_history: Dictionary<any>[];

  constructor(data: Dictionary<any>) {
    this.$_history = [cloneDeep(data)];
  }

  first() {
    return this.$_history[0];
  }

  last() {
    return this.$_history[this.$_history.length - 1];
  }

  reset(data: Dictionary<any>) {
    this.$_history.splice(0, this.$_history.length);
    this.$_history.push(cloneDeep(data));
  }
}

function isResourceLike(value: any): value is IResource {
  return (
    isString(get(value, 'id')) &&
    isString(get(value, 'etag')) &&
    isObject(get(value, 'links'))
  );
}

function isResourceContainerLike(value: any): value is ResourceContainer {
  return (
    get(value, 'object') === 'list' &&
    isString(get(value, 'self')) &&
    isArray(get(value, 'data'))
  );
}

function isEmbeddedList(value: any) {
  return get(value, 'object') === 'list' && isArray(get(value, 'data'));
}

interface ResourceOptions {
  parentURL?: string;
}

interface IResource {
  id: string;
  etag: string;
  links: object;
}

export interface ResourceContainer {
  object: 'list' | 'object';
  self: string;
  data: object[] | object;
}

const isPrivateKey = (key: string) => startsWith(key, '$');

export class Resource {
  readonly $_history: ResourceHistory;
  readonly $_container?: ResourceContainer;
  private $_editor: ResourceEditor | null = null;

  id?: string;
  etag?: string;
  locked?: boolean;

  constructor(data: Dictionary<any>, defaults?: Dictionary<any>) {
    if (defaults) {
      for (let [k, v] of Object.entries(defaults)) {
        const current_value = get(data, k);
        if (current_value == null) {
          set(data, k, v);
        }
      }
    }

    this.$_history = new ResourceHistory(data);
    this.init(data);
  }

  get isNew() {
    return this.id == null;
  }

  get isReadOnly() {
    return this.locked === true;
  }

  get isReadonly() {
    return this.locked === true;
  }

  snapshot(field: string, defaultValue: any) {
    return get(this.$_history.last(), field, defaultValue);
  }

  getData(options?: {
    skipNulls?: boolean;
    exclude?: Array<string>;
    fields?: Array<string>;
  }) {
    let result: Dictionary<any> = {};

    const skipNulls = options && options.skipNulls;
    const exclude = options?.exclude;
    const fields = options?.fields;

    // iterating througn the resource's own enumerable properties
    for (const [key, value] of Object.entries(this)) {
      if (isPrivateKey(key) || isFunction(value)) {
        continue;
      }

      if (value === null && skipNulls) {
        continue;
      }

      if (fields && fields.indexOf(key) == -1) {
        continue;
      }

      if (exclude && exclude.indexOf(key) > -1) {
        continue;
      }

      result[key] = value;
    }

    return result;
  }

  isValueChanged(key: string) {
    const first = this.$_history.first();
    console.log(get(this, key), first[key]);

    return !isEqual(get(this, key), first[key]);
  }

  getChanges(
    options: {
      skipEmbeddedLists: boolean;
    } = {
      skipEmbeddedLists: false,
    },
  ) {
    const first = this.$_history.first();

    let result: Dictionary<any> = {};

    // iterating througn the resource's own enumerable properties
    for (const [key, value] of Object.entries(this)) {
      if (options.skipEmbeddedLists && isEmbeddedList(value)) {
        continue;
      }

      if (isPrivateKey(key) || isFunction(value)) {
        continue;
      }
      if (!isEqual(value, first[key])) {
        result[key] = value;
      }
    }
    return result;
  }

  getURL() {
    const result = get(this, 'self', get(this, 'links.self'));
    if (!isString(result)) {
      throw new Error('Missing URL data.');
    }
    return result;
  }

  getETAG() {
    const result = get(this, '$etag', get(this, 'etag'));
    if (!isString(result)) {
      throw new Error('Missing ETAG data.');
    }
    return result;
  }

  reset(data: Dictionary<any>) {
    this.$_history.reset(data);

    for (const [key, value] of Object.entries(this)) {
      if (isPrivateKey(key) || isFunction(value)) {
        continue;
      }
      set(this, key, undefined);
    }
    extend(this, data);

    if (this.$_editor) {
      this.$_editor.reset(data);
    }
  }

  editor() {
    if (!this.$_editor) {
      this.$_editor = new ResourceEditor(this);
    }
    return this.$_editor;
  }

  clone(overrides?: Dictionary<any>) {
    return new Resource(extend(this.getData(), overrides));
  }

  private init(data: Dictionary<any>) {
    for (const [key, value] of Object.entries(data)) {
      if (isPrivateKey(key)) {
        continue;
      }

      if (
        isString(value) ||
        isNumber(value) ||
        value == null ||
        isBoolean(value)
      ) {
        set(this, key, value);
        continue;
      }

      if (isResourceContainerLike(value)) {
        set(this, key, {
          ...value,
          data: map(value.data, (d) => new Resource(d)),
        });
        continue;
      }

      set(this, key, value);
    }
  }
}

export class ResourceEditor extends Resource {
  readonly $_resource: Resource;

  constructor(resource: Resource) {
    super(resource.getData());
    this.$_resource = resource;
  }
}

export function isResourceEditor(value: any): value is ResourceEditor {
  return value instanceof ResourceEditor;
}
